import { useId, type ReactNode } from 'react';
import { cn } from '@/lib/utils';

/**
 * Interruptor de ligar/desligar.
 *
 * É um `<input type="checkbox">` de verdade, transparente e estilizado pelo `peer`. A
 * alternativa comum — uma `div` com `role="switch"` e `onClick` — obriga a reimplementar barra
 * de espaço, foco, `form` e o estado indeterminado, e alguma dessas peças sempre fica de fora.
 * Aqui o navegador faz tudo isso de graça.
 *
 * O campo cobre o desenho inteiro (transparente, por cima), e não `sr-only`: com `sr-only` o
 * clique no desenho não chegava a ele, e o interruptor sem rótulo — a célula de tabela, como
 * "Vê custo e margem" em Membros — não ligava (17/10/2026). `display:none` também não serve:
 * tira o campo da ordem de tabulação.
 */
export function Switch({
  checked,
  onCheckedChange,
  disabled = false,
  id,
  'aria-describedby': describedBy,
  'aria-label': label,
}: {
  checked: boolean;
  onCheckedChange: (checked: boolean) => void;
  disabled?: boolean;
  id?: string;
  'aria-describedby'?: string;
  /** Para o interruptor sem rótulo visível, como numa célula de tabela. */
  'aria-label'?: string;
}) {
  return (
    <span className="relative inline-flex shrink-0">
      <input
        id={id}
        type="checkbox"
        role="switch"
        checked={checked}
        disabled={disabled}
        aria-describedby={describedBy}
        aria-label={label}
        onChange={(change) => onCheckedChange(change.target.checked)}
        className="peer absolute inset-0 z-10 m-0 cursor-pointer appearance-none opacity-0 disabled:cursor-not-allowed"
      />
      <span
        aria-hidden
        className={cn(
          'h-6 w-11 rounded-full bg-input transition-colors',
          'peer-checked:bg-primary',
          'peer-focus-visible:outline-none peer-focus-visible:ring-2 peer-focus-visible:ring-ring peer-focus-visible:ring-offset-2',
          'peer-disabled:cursor-not-allowed peer-disabled:opacity-50',
        )}
      />
      <span
        aria-hidden
        className={cn(
          'pointer-events-none absolute left-0.5 top-0.5 size-5 rounded-full bg-background shadow transition-transform',
          'peer-checked:translate-x-5',
        )}
      />
    </span>
  );
}

/**
 * Linha de interruptor: rótulo, explicação e o controle à direita.
 *
 * A explicação é opcional e existe porque quase toda chave destas telas precisa de uma —
 * um rótulo curto raramente diz o que acontece ao ligar. Ela é
 * ligada por `aria-describedby`, e não só posicionada embaixo: quem usa leitor de tela
 * ouviria o rótulo sozinho e teria a mesma dúvida.
 *
 * O rótulo inteiro é clicável (é um `<label>`), o que dá uma área de toque generosa no
 * celular — um alvo de 44px em vez dos 24px do interruptor.
 */
export function ToggleField({
  label,
  description,
  checked,
  onCheckedChange,
  disabled = false,
  className,
}: {
  label: string;
  description?: ReactNode;
  checked: boolean;
  onCheckedChange: (checked: boolean) => void;
  disabled?: boolean;
  className?: string;
}) {
  const id = useId();
  const descriptionId = `${id}-description`;

  return (
    <label
      htmlFor={id}
      className={cn(
        'flex cursor-pointer items-start justify-between gap-4 py-2',
        disabled && 'cursor-not-allowed opacity-60',
        className,
      )}
    >
      <span className="min-w-0">
        <span className="block text-sm font-medium">{label}</span>
        {description && (
          <span id={descriptionId} className="mt-0.5 block text-xs text-muted-foreground">
            {description}
          </span>
        )}
      </span>

      <Switch
        id={id}
        checked={checked}
        onCheckedChange={onCheckedChange}
        disabled={disabled}
        aria-describedby={description ? descriptionId : undefined}
      />
    </label>
  );
}
